// Real Firebase Events Service
// Replaces mockEvents.ts with Firestore operations

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

export interface PostEventData {
  guestCount?: number;
  venueName?: string;
  venueType?: string;
  actualDurationMinutes?: number;
  notes?: string;
  collectedAt?: string;
}
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { GUEST_TIERS } from '../utils/eventTiers';
import type { PlayMode } from './playModes';
import { buildClearShowStopUpdate, parseShowStop } from '../utils/lastSong';

export interface EventContactInfo {
  name?: string;
  phone?: string;
  email?: string;
}

export interface EventSizeGate {
  applies: boolean;
  gateEnabled: boolean;
  tierCap: number;
  pendingTierCap: number | null;
  pendingSince: string | null;
  lastTierChangeAt: string | null;
  lastTierChangeBy: 'auto' | 'owner' | null;
}

export interface ShowStop {
  stoppedAt: string;
  requestId: string;
  playerPaused: boolean;
}

export interface Event {
  id: string;
  djId: string;
  name: string;
  date: string;
  startTime: string;
  startDate?: string;   // YYYY-MM-DD, defaults to date if not set
  // The scheduled start as an instant, worked out in the DJ's browser; the server reads it for the pending-requests email (ITER-028)
  startAtMs?: number | null;
  endTime?: string;
  endDate?: string;     // YYYY-MM-DD, defaults to date if not set (next day for past-midnight events)
  location?: string;
  address?: string;
  description?: string;
  contactInfo?: EventContactInfo;
  status: 'upcoming' | 'active' | 'completed';
  eventUrl: string;
  qrCodeUrl?: string;
  spotifyPlaylistUrl?: string;
  spotifyUnplayedPlaylistUrl?: string;
  settings: EventSettings;
  editMode: boolean;
  flagged?: boolean;
  locked?: boolean;
  lockedAt?: string;
  archived?: boolean;
  archivedAt?: string;
  postEventData?: PostEventData;
  uniqueVoterCount?: number;
  participantCount?: number;
  autoApprovalHold?: boolean;
  lastPlaybackAt?: string;
  requestsStartedAt?: string;
  playModeLockedAt?: string;
  deletedAt?: string;
  guestCount?: number;
  sizeGate?: EventSizeGate;
  lastSongRequestId?: string | null;
  endAfterLastSong?: boolean;
  showStop?: ShowStop | null;
  createdAt: string;
  updatedAt: string;
}

export type PreEventQueueView = 'full' | 'own_only' | 'blind';
export type PostCloseQueueView = 'show' | 'hide';
export type DuringEventQueueView = 'now_and_upcoming' | 'now_only' | 'hide';

export interface EventSettings {
  playMode?: PlayMode;
  blockExplicit: boolean;
  profanityFilter: boolean;
  requireApproval: boolean;
  warnExplicit: boolean;
  votingEnabled: boolean;
  requestsOpen: boolean;
  maxRequestsPerUser: number;
  postCloseRequestLimit: number;
  autoApprove: boolean;
  cooldownMinutes: number;
  votingClosed: boolean;
  votingClosedAt?: string;
  votingCloseTime?: string;
  votingOpenTime?: string;
  votingScheduledToOpen?: boolean;
  // Close requests at the scheduled start (ITER-137); requestsCloseAtMs is the derived instant, null after a reopen
  closeRequestsAtStart?: boolean;
  requestsCloseAtMs?: number | null;
  maxQueueSize?: number;
  queueMode?: 'standard' | 'ranked' | 'fifo';
  rankedScoringMode?: 'consensus' | 'discovery';
  rankingMinSongs?: number;
  rankingMaxSongs?: number;
  rankingMinParticipants?: number;
  allowTips?: boolean;
  filterExplicit?: boolean;
  preEventQueueView?: PreEventQueueView;
  postCloseQueueView?: PostCloseQueueView;
  duringEventQueueView?: DuringEventQueueView;
  // On-site verification
  onSiteVerificationEnabled?: boolean;
  onSiteVerificationToken?: string;
  onSiteVerificationEnabledAt?: string;
  // Downvoting
  downvotingEnabled?: boolean;
  // Crowd vote-out (ITER-144) and crowd favorites (ITER-075); locked with the play mode
  voteOutEnabled?: boolean;
  voteOutThresholdPercent?: number;
  upvoteOverrideEnabled?: boolean;
  upvoteOverridePercent?: number;
  // Queue time breakpoint
  allowOverlap?: boolean;
  // Crossfade (seconds) — matches Spotify's crossfade setting
  crossfadeSeconds?: number;
  // Ranking size (replaces rankingMinSongs/rankingMaxSongs)
  rankSize?: number;
  // Staleness timeout (minutes) — withhold rankings from inattentive attendees
  rankingStalenessMinutes?: number;
  // Auto-reject songs the DJ has previously blocked in Song Library
  autoRejectBlocked?: boolean;
  // Doom Voting (vote rate limiting)
  doomVotingEnabled?: boolean;
  doomVotingMaxVotes?: number;
  doomVotingActiveMinutes?: number;
  doomVotingRestMinutes?: number;
  // Super Request (The People's Pick)
  superRequestEnabled?: boolean;
  superRequestThresholdType?: 'count' | 'percentage';
  superRequestThresholdCount?: number;
  superRequestThresholdPercent?: number;
  maxNominationsPerUser?: number;
  // Attendee song flagging
  allowAttendeeFlagging?: boolean;
  // Attendee Vote tab visibility (DJ override for ranked-mode events)
  attendeeVoteTab?: boolean;
  // VIP Trigger
  vipTriggerEnabled?: boolean;
  // Co-Approval
  coApprovalEnabled?: boolean;
  // DJ pending-requests email (ITER-028); on unless false
  pendingEmailAlerts?: boolean;
  // Local Files
  localFilesEnabled?: boolean;
  // Queue depth — how many songs to keep queued in the player at once (0–10, default 1; 0 = queue one song at a time)
  queueDepth?: number;
  // Queue Next also locks the song (ITER-171); auto-lock cap = max(queueDepth, 1)
  lockWhenQueued?: boolean;
  // Cross-event match propagation — when a song is matched in another event, auto-apply here
  autoApplyMatchFromOtherEvents?: boolean;
  overflowEnabled?: boolean;
  prizeTimeoutMinutes?: number;
  hideRankingInfo?: boolean;
  // Lottery
  lotteryEnabled?: boolean;
  lotteryMode?: 'one_time' | 'recurring';
  lotteryIntervalMinutes?: number;
  lotteryPrizeType?: 'pick_next_song' | 'auto_queue_bypass' | 'custom' | 'shoutout';
  lotteryPrizeCustomText?: string;
  lotteryAllowRepeatWinners?: boolean;
  // Prediction Game
  predictionGameActive?: boolean;
  predictionGameId?: string;
  // Trivia Game
  triviaEnabled?: boolean;
  triviaGameActive?: boolean;
  triviaGameId?: string;
}

function timestampToIso(value: unknown): string | null {
  return value instanceof Timestamp ? value.toDate().toISOString() : null;
}

function docToSizeGate(raw: unknown): EventSizeGate | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const value = raw as Record<string, unknown>;
  return {
    applies: value.applies === true,
    gateEnabled: value.gateEnabled !== false,
    tierCap: typeof value.tierCap === 'number' ? value.tierCap : GUEST_TIERS[0],
    pendingTierCap: typeof value.pendingTierCap === 'number' ? value.pendingTierCap : null,
    pendingSince: timestampToIso(value.pendingSince),
    lastTierChangeAt: timestampToIso(value.lastTierChangeAt),
    lastTierChangeBy: value.lastTierChangeBy === 'auto' || value.lastTierChangeBy === 'owner' ? value.lastTierChangeBy : null,
  };
}

// Helper to convert Firestore doc to Event
export function docToEvent(docSnap: any): Event {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    djId: data.djId,
    name: data.name,
    date: data.date,
    startTime: data.startTime,
    startDate: data.startDate || undefined,
    startAtMs: typeof data.startAtMs === 'number' ? data.startAtMs : null,
    endTime: data.endTime || undefined,
    endDate: data.endDate || undefined,
    location: data.location || undefined,
    address: data.address || undefined,
    description: data.description || undefined,
    contactInfo: data.contactInfo || undefined,
    status: data.status || 'upcoming',
    eventUrl: data.eventUrl,
    qrCodeUrl: data.qrCodeUrl || undefined,
    spotifyPlaylistUrl: data.spotifyPlaylistUrl || undefined,
    spotifyUnplayedPlaylistUrl: data.spotifyUnplayedPlaylistUrl || undefined,
    settings: data.settings || {},
    editMode: data.editMode || false,
    flagged: data.flagged || false,
    locked: data.locked || false,
    lockedAt: data.lockedAt || undefined,
    archived: data.archived || false,
    archivedAt: data.archivedAt || undefined,
    postEventData: data.postEventData || undefined,
    uniqueVoterCount: data.uniqueVoterCount || undefined,
    participantCount: typeof data.participantCount === 'number' ? data.participantCount : undefined,
    autoApprovalHold: data.autoApprovalHold === true,
    lastPlaybackAt: data.lastPlaybackAt instanceof Timestamp ? data.lastPlaybackAt.toDate().toISOString() : undefined,
    requestsStartedAt: timestampToIso(data.requestsStartedAt) ?? undefined,
    playModeLockedAt: timestampToIso(data.playModeLockedAt) ?? undefined,
    deletedAt: timestampToIso(data.deletedAt) ?? undefined,
    guestCount: typeof data.guestCount === 'number' ? data.guestCount : undefined,
    sizeGate: docToSizeGate(data.sizeGate),
    lastSongRequestId: typeof data.lastSongRequestId === 'string' && data.lastSongRequestId ? data.lastSongRequestId : null,
    endAfterLastSong: data.endAfterLastSong === true,
    showStop: parseShowStop(data.showStop),
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
  };
}

export function getEventStartDate(event: Pick<Event, 'date' | 'startDate' | 'startTime'> | null | undefined): Date | null {
  const startDateStr = event?.startDate || event?.date;
  if (!startDateStr || !event?.startTime) return null;
  const [syr, smo, sdy] = startDateStr.split('-').map(Number);
  const [startHour, startMin] = event.startTime.split(':').map(Number);
  const eventStart = new Date(syr, smo - 1, sdy, startHour, startMin, 0, 0);
  return Number.isNaN(eventStart.getTime()) ? null : eventStart;
}

export function eventStartAtMs(fields: Pick<Event, 'date' | 'startDate' | 'startTime'>): number | null {
  return getEventStartDate(fields)?.getTime() ?? null;
}

// Compute event status based on current time
export function computeEventStatus(event: Event): 'upcoming' | 'active' | 'completed' {
  if (event.status === 'completed') return 'completed';

  const now = new Date();
  const eventStart = getEventStartDate(event);

  let eventEnd: Date | null = null;
  if (event.endTime) {
    const endDateStr = event.endDate || event.date;
    const [eyr, emo, edy] = endDateStr.split('-').map(Number);
    const [endHour, endMin] = event.endTime.split(':').map(Number);
    eventEnd = new Date(eyr, emo - 1, edy, endHour, endMin, 0, 0);
  }

  if (eventStart && now < eventStart) return 'upcoming';
  if (eventEnd && now > eventEnd) return 'completed';
  return 'active';
}

// Check if any engagement game mode is currently active
export function isGameplayActive(settings: EventSettings | undefined): boolean {
  if (!settings) return false;
  return (
    settings.predictionGameActive === true ||
    settings.lotteryEnabled === true ||
    settings.triviaGameActive === true
  );
}

// Generate QR code URL
export function generateQrCodeUrl(eventUrl: string): string {
  const fullUrl = `${window.location.origin}/e/${eventUrl}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(fullUrl)}`;
}

// Generate a URL-friendly slug
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 30)
    + '-' + Math.random().toString(36).substring(2, 6);
}

// Check if voting should be automatically opened
async function checkAndAutoOpenVoting(event: Event): Promise<Event> {
  if (!event.settings.votingScheduledToOpen || !event.settings.votingOpenTime) return event;
  if (event.settings.requestsOpen && !event.settings.votingClosed) return event;

  const openTime = new Date(event.settings.votingOpenTime).getTime();
  if (Date.now() >= openTime) {
    const updatedSettings = {
      ...event.settings,
      votingClosed: false,
      requestsOpen: true,
      votingScheduledToOpen: false,
    };
    try {
      await updateDoc(doc(db, 'events', event.id), {
        settings: updatedSettings,
        updatedAt: serverTimestamp(),
      });
    } catch {
      // Attendees don't have write permission — still return updated view
    }
    return { ...event, settings: updatedSettings, updatedAt: new Date().toISOString() };
  }
  return event;
}

// Check if voting should be automatically closed
async function checkAndAutoCloseVoting(event: Event): Promise<Event> {
  if (event.settings.votingClosed || !event.settings.votingCloseTime) return event;

  const closeTime = new Date(event.settings.votingCloseTime).getTime();
  if (Date.now() >= closeTime) {
    const updatedSettings = {
      ...event.settings,
      votingClosed: true,
      votingClosedAt: event.settings.votingCloseTime,
      requestsOpen: false,
    };
    try {
      await updateDoc(doc(db, 'events', event.id), {
        settings: updatedSettings,
        updatedAt: serverTimestamp(),
      });
    } catch {
      // Attendees don't have write permission — still return updated view
    }
    return { ...event, settings: updatedSettings, updatedAt: new Date().toISOString() };
  }
  return event;
}

// Get events for a specific DJ
export async function getEventsByDjId(djId: string): Promise<Event[]> {
  const q = query(
    collection(db, 'events'),
    where('djId', '==', djId),
    orderBy('date', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => {
    const event = docToEvent(d);
    return { ...event, status: computeEventStatus(event) };
  });
}

// Get a single event by ID
export async function getEventById(eventId: string): Promise<Event | null> {
  const docSnap = await getDoc(doc(db, 'events', eventId));
  if (!docSnap.exists() || docSnap.data().deletedAt != null) return null;

  let event = docToEvent(docSnap);
  event = await checkAndAutoOpenVoting(event);
  event = await checkAndAutoCloseVoting(event);
  return { ...event, status: computeEventStatus(event) };
}

// Get a single event by URL slug
export async function getEventByUrl(eventUrl: string): Promise<Event | null> {
  const q = query(
    collection(db, 'events'),
    where('eventUrl', '==', eventUrl),
    limit(1)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty || snapshot.docs[0].data().deletedAt != null) return null;

  let event = docToEvent(snapshot.docs[0]);
  event = await checkAndAutoOpenVoting(event);
  event = await checkAndAutoCloseVoting(event);
  return { ...event, status: computeEventStatus(event) };
}

// Create a new event
export async function createEvent(
  djId: string,
  data: {
    name: string;
    date: string;
    startTime: string;
    startDate?: string;
    endTime?: string;
    endDate?: string;
    location?: string;
    address?: string;
    description?: string;
    contactInfo?: EventContactInfo;
    playMode: PlayMode;
    settings?: Partial<EventSettings>;
  }
): Promise<Event> {
  const defaultSettings: EventSettings = {
    blockExplicit: true,
    profanityFilter: true,
    requireApproval: false,
    warnExplicit: true,
    votingEnabled: true,
    requestsOpen: true,
    maxRequestsPerUser: 5,
    postCloseRequestLimit: 2,
    autoApprove: true,
    cooldownMinutes: 5,
    votingClosed: false,
    maxQueueSize: 50,
    queueMode: 'standard',
    rankedScoringMode: 'consensus',
    rankingMinParticipants: 3,
    allowTips: true,
    filterExplicit: false,
  };

  const eventData = {
    djId,
    name: data.name,
    date: data.date,
    startTime: data.startTime,
    startDate: data.startDate || data.date,
    startAtMs: eventStartAtMs({ date: data.date, startDate: data.startDate, startTime: data.startTime }),
    endTime: data.endTime || null,
    endDate: data.endDate || data.date,
    location: data.location || null,
    address: data.address || null,
    description: data.description || null,
    contactInfo: data.contactInfo ? stripUndefined(data.contactInfo) : null,
    status: 'upcoming',
    eventUrl: generateSlug(data.name),
    qrCodeUrl: null,
    editMode: false,
    settings: { ...defaultSettings, ...data.settings, playMode: data.playMode },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, 'events'), eventData);
  const now = new Date().toISOString();

  return {
    id: docRef.id,
    djId,
    name: data.name,
    date: data.date,
    startTime: data.startTime,
    endTime: data.endTime,
    location: data.location,
    description: data.description,
    status: 'upcoming',
    eventUrl: eventData.eventUrl,
    editMode: false,
    settings: eventData.settings,
    createdAt: now,
    updatedAt: now,
  };
}

export async function setEventPlayMode(eventId: string, mode: PlayMode): Promise<void> {
  await updateDoc(doc(db, 'events', eventId), {
    'settings.playMode': mode,
    updatedAt: serverTimestamp(),
  });
}

// Last Song (ITER-175): Bridge pauses the player when this request ends; null clears the mark
export async function setLastSong(eventId: string, requestId: string | null): Promise<void> {
  await updateDoc(doc(db, 'events', eventId), {
    lastSongRequestId: requestId,
    endAfterLastSong: false,
    updatedAt: serverTimestamp(),
  });
}

export async function resumeShow(eventId: string): Promise<void> {
  await updateDoc(doc(db, 'events', eventId), {
    ...buildClearShowStopUpdate(),
    endAfterLastSong: false,
    updatedAt: serverTimestamp(),
  });
}

// Auto-end (ITER-199): the onShowStopEndEvent trigger ends the event when Bridge writes the stop
export async function setEndAfterLastSong(eventId: string, value: boolean): Promise<void> {
  await updateDoc(doc(db, 'events', eventId), {
    endAfterLastSong: value,
    updatedAt: serverTimestamp(),
  });
}

// Dotted paths, so the rest of the settings map is never overwritten from a stale copy
export async function updateEventSettingFields(
  eventId: string,
  fields: Partial<Pick<EventSettings, 'queueDepth' | 'lockWhenQueued'>>,
): Promise<void> {
  const dottedFields: Record<string, number | boolean> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) dottedFields[`settings.${key}`] = value;
  }
  await updateDoc(doc(db, 'events', eventId), {
    ...dottedFields,
    updatedAt: serverTimestamp(),
  });
}

// Start time and the derived close instant (ITER-137) in one write; the close instant is a dotted path so the rest of settings is kept
export async function updateEventStart(
  eventId: string,
  fields: { startTime: string; startDate: string; requestsCloseAtMs?: number | null },
): Promise<Event> {
  const eventRef = doc(db, 'events', eventId);
  const closeAtField = fields.requestsCloseAtMs !== undefined
    ? { 'settings.requestsCloseAtMs': fields.requestsCloseAtMs }
    : {};
  await updateDoc(eventRef, {
    startTime: fields.startTime,
    startDate: fields.startDate,
    startAtMs: eventStartAtMs({ date: fields.startDate, startDate: fields.startDate, startTime: fields.startTime }),
    ...closeAtField,
    updatedAt: serverTimestamp(),
  });

  const updated = await getDoc(eventRef);
  const event = docToEvent(updated);
  return { ...event, status: computeEventStatus(event) };
}

// Update an event
// Recursively remove undefined values from an object (Firestore rejects undefined)
function stripUndefined(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date) && typeof value.toDate !== 'function') {
      result[key] = stripUndefined(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

export async function updateEvent(
  eventId: string,
  data: Partial<Omit<Event, 'id' | 'djId' | 'createdAt'>>
): Promise<Event> {
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, {
    ...stripUndefined(data as Record<string, any>),
    updatedAt: serverTimestamp(),
  });

  const updated = await getDoc(eventRef);
  const event = docToEvent(updated);
  return { ...event, status: computeEventStatus(event) };
}

export async function endEvent(eventId: string): Promise<void> {
  await updateEvent(eventId, { status: 'completed' });
}

export async function reopenEvent(eventId: string): Promise<void> {
  await updateEvent(eventId, { status: 'active' });
}

export async function lockEvent(eventId: string): Promise<void> {
  await updateEvent(eventId, { locked: true, lockedAt: new Date().toISOString() });
}

export async function unlockEvent(eventId: string): Promise<void> {
  await updateEvent(eventId, { locked: false });
}

export async function archiveEvent(eventId: string): Promise<void> {
  await updateEvent(eventId, {
    archived: true,
    archivedAt: new Date().toISOString(),
    locked: true,
    lockedAt: new Date().toISOString(),
  });
}

export async function unarchiveEvent(eventId: string): Promise<void> {
  await updateEvent(eventId, { archived: false });
}

export async function savePostEventData(eventId: string, data: PostEventData): Promise<void> {
  await updateEvent(eventId, {
    postEventData: { ...data, collectedAt: new Date().toISOString() },
  });
}

export async function getArchivedEvents(djId: string): Promise<Event[]> {
  const q = query(
    collection(db, 'events'),
    where('djId', '==', djId),
    where('archived', '==', true),
    orderBy('archivedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToEvent);
}

// Detach an event from its DJ. The server keeps the event and everything tied to it (ITER-154)
export async function deleteEvent(eventId: string): Promise<void> {
  const callable = httpsCallable<{ eventId: string }, { ok: boolean }>(functions, 'deleteDjEvent');
  await callable({ eventId });
}

// Check if a DJ owns an event
export async function isDjEventOwner(eventId: string, djId: string): Promise<boolean> {
  const event = await getEventById(eventId);
  return event?.djId === djId;
}

// Generate QR code for an event
export async function generateEventQrCode(eventId: string): Promise<string> {
  const event = await getEventById(eventId);
  if (!event) throw new Error('Event not found');

  const qrCodeUrl = generateQrCodeUrl(event.eventUrl);
  await updateDoc(doc(db, 'events', eventId), {
    qrCodeUrl,
    updatedAt: serverTimestamp(),
  });

  return qrCodeUrl;
}

// Generate a random verification token (32 hex chars)
export function generateVerificationToken(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}

// Generate QR code URL for on-site verification
export function generateVerificationQrCodeUrl(eventUrl: string, token: string): string {
  const fullUrl = `${window.location.origin}/e/${eventUrl}?verify=${token}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(fullUrl)}`;
}

// Enable on-site verification for an event
export async function enableOnSiteVerification(eventId: string): Promise<string> {
  const event = await getEventById(eventId);
  if (!event) throw new Error('Event not found');

  const token = generateVerificationToken();
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, {
    'settings.onSiteVerificationEnabled': true,
    'settings.onSiteVerificationToken': token,
    'settings.onSiteVerificationEnabledAt': new Date().toISOString(),
    updatedAt: serverTimestamp(),
  });

  return token;
}

// Disable on-site verification for an event
export async function disableOnSiteVerification(eventId: string): Promise<void> {
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, {
    'settings.onSiteVerificationEnabled': false,
    updatedAt: serverTimestamp(),
  });
}

// Regenerate verification token (revokes all existing verifications)
export async function regenerateVerificationToken(eventId: string): Promise<string> {
  const { revokeAllVerifications } = await import('./verification');
  await revokeAllVerifications(eventId);

  const token = generateVerificationToken();
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, {
    'settings.onSiteVerificationToken': token,
    'settings.onSiteVerificationEnabledAt': new Date().toISOString(),
    updatedAt: serverTimestamp(),
  });

  return token;
}

// Network simulation stubs (no-ops for real Firebase)
export function setNetworkFailure(_enabled: boolean): void {}
export function isNetworkFailureSimulated(): boolean { return false; }
export function setSlowNetwork(_enabled: boolean): void {}
export function isSlowNetworkSimulated(): boolean { return false; }
